import { Textarea } from '@folio/components/forms/textarea';
import { Button } from '@folio/components/ui/button';
import { Group, Stack } from '@folio/components/ui/flex';
import { Popover, PopoverContent, PopoverTrigger } from '@folio/components/ui/popover';
import { Text } from '@folio/components/ui/text';
import { formatDateWithTime } from '@folio/lib';
import { Loader2Icon } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function CorrectionPopover({
  title,
  withReason = true,
  canSave,
  isPending,
  initialReason = '',
  onSave,
  onOpenChange,
  trigger,
  children,
}: {
  title: string;
  withReason?: boolean;
  canSave: boolean;
  isPending?: boolean;
  initialReason?: string;
  onSave: (reason: string | undefined) => void;
  onOpenChange?: (open: boolean) => void;
  trigger?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(initialReason);

  const changeOpen = (next: boolean) => {
    if (next) setReason(initialReason);
    setOpen(next);
    onOpenChange?.(next);
  };

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <Button
            type="button"
            variant="link"
            size="xs"
            className="text-foreground underline"
            aria-label={title}
            aria-busy={isPending}
            disabled={isPending}
          >
            {isPending ? (
              <Loader2Icon className="h-3 w-3 animate-spin" />
            ) : (
              t('documentReview.correct')
            )}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" aria-label={title} className="w-80 p-3">
        <Stack gap={3}>
          <Text size="sm" weight="semibold" tone="default">
            {title}
          </Text>
          {children}
          {withReason && (
            <Textarea
              aria-label={t('documentReview.correction.reasonPlaceholder')}
              placeholder={t('documentReview.correction.reasonPlaceholder')}
              value={reason}
              onChange={event => setReason(event.target.value)}
            />
          )}
          <Group justify="end" gap={2}>
            <Button type="button" variant="ghost" size="sm" onClick={() => changeOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!canSave || isPending}
              onClick={() => {
                onSave(reason.trim() || undefined);
                changeOpen(false);
              }}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </PopoverContent>
    </Popover>
  );
}

export function CorrectionTrace({
  correctedAt,
  correctedByEmail,
  reason,
}: {
  correctedAt: string;
  correctedByEmail?: string;
  reason?: string;
}) {
  const { t } = useTranslation();
  const date = formatDateWithTime(new Date(correctedAt));

  return (
    <Stack gap={1}>
      <Text size="xs" tone="muted">
        {correctedByEmail
          ? t('documentReview.correction.correctedBy', { email: correctedByEmail, date })
          : t('documentReview.correction.correctedAt', { date })}
      </Text>
      {reason && (
        <Text size="sm" tone="default" className="whitespace-pre-wrap">
          {reason}
        </Text>
      )}
    </Stack>
  );
}
